import { Popover } from '@scholastic/mockingjay';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';
import { cn } from '@data/utils/cn/cn';
import { formatMetric } from '@data/utils/format-metric';
import type { MetricFormat } from '@data/hooks/api/use-metrics-v2-api/use-metrics-v2-api';
import InfoIcon from '@data/assets/info-icon.svg';
import { formatAssessmentDate } from '@data/utils/format-assessment-date';

export type MetricCardVariant = 'primary' | 'secondary';

interface MetricCardProps {
  value: string | null;
  label: string;
  popoverContent?: string;
  className?: string;
  isLoading?: boolean;
  variant?: MetricCardVariant;
  format?: MetricFormat;
  dateOfAssessment?: string;
}

export function MetricCard({
  value,
  label,
  popoverContent,
  className,
  isLoading,
  variant = 'primary',
  format = 'number',
  dateOfAssessment,
}: MetricCardProps) {
  return (
    <div
      className={cn(
        'text-center',
        variant === 'primary' &&
          'flex min-h-[96px] flex-col justify-center rounded-lg border border-neutral-200 p-2',
        variant === 'primary' && (isLoading ? 'bg-white' : 'bg-purple-50'),
        variant === 'secondary' && 'mb-4 p-2 @[523px]:mb-0',
        className
      )}
    >
      {isLoading ? (
        <div
          role="status"
          aria-busy="true"
          aria-label="Loading metric"
          className="flex flex-col items-center gap-4"
        >
          <LoadingBar className="mx-0 w-[67px] self-center" />
          <LoadingBar />
        </div>
      ) : (
        <>
          {variant === 'primary' &&
            dateOfAssessment &&
            value &&
            value !== 'null' && (
              <div className="text-xs text-neutral-700">
                As of {formatAssessmentDate(dateOfAssessment)} Assessment
              </div>
            )}
          <div
            className={cn(
              'text-3xl font-semibold',
              variant === 'secondary' && 'text-blue-700'
            )}
          >
            {formatMetric(value, format)}
          </div>
          <div className="mx-auto max-w-[250px] text-center text-sm leading-6">
            {label}
            {popoverContent && (
              <Popover
                trigger={
                  <button
                    aria-label={`More information about ${label}`}
                    type="button"
                    className="ml-1 inline-flex translate-y-[-1px] align-middle"
                  >
                    <InfoIcon aria-hidden="true" fill="white" />
                  </button>
                }
              >
                {popoverContent}
              </Popover>
            )}
          </div>
        </>
      )}
    </div>
  );
}
